(() => {
  const debounce = (callback, delay = 300) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  const disableUnconfiguredLinks = (root) => {
    root.querySelectorAll('a[aria-disabled="true"]').forEach((link) => {
      link.addEventListener('click', (event) => event.preventDefault());
    });
  };

  const initCollection = (root) => {
    disableUnconfiguredLinks(root);
    const input = root.querySelector('[data-btl-card-search]');
    const empty = root.querySelector('[data-btl-search-empty]');
    if (!input) return;

    const filterCards = () => {
      const query = input.value.trim().toLowerCase();
      const cards = [...root.querySelectorAll('[data-btl-product-card]')];
      let visibleCount = 0;
      cards.forEach((card) => {
        const matches = !query || (card.dataset.btlSearchText || '').includes(query);
        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });
      if (empty) empty.hidden = visibleCount !== 0 || cards.length === 0;
    };

    input.addEventListener('input', debounce(filterCards));

    root.querySelectorAll('[data-btl-filter-form] select').forEach((select) => {
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  };

  const initProduct = (root) => {
    disableUnconfiguredLinks(root);
    const slides = [...root.querySelectorAll('[data-btl-slide]')];
    const dots = [...root.querySelectorAll('[data-btl-gallery-dot]')];
    const gallery = root.querySelector('[data-btl-gallery]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const showSlide = (index) => {
      if (!slides.length) return;
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    root.querySelector('[data-btl-gallery-prev]')?.addEventListener('click', () => showSlide(activeIndex - 1));
    root.querySelector('[data-btl-gallery-next]')?.addEventListener('click', () => showSlide(activeIndex + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => showSlide(Number(dot.dataset.btlGalleryDot))));

    if (gallery) {
      gallery.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') showSlide(activeIndex - 1);
        if (event.key === 'ArrowRight') showSlide(activeIndex + 1);
      });
      let touchStart = null;
      gallery.addEventListener('touchstart', (event) => {
        touchStart = event.changedTouches[0]?.clientX ?? null;
      }, { passive: true });
      gallery.addEventListener('touchend', (event) => {
        if (touchStart === null) return;
        const distance = (event.changedTouches[0]?.clientX ?? touchStart) - touchStart;
        if (Math.abs(distance) > 50) showSlide(activeIndex + (distance < 0 ? 1 : -1));
        touchStart = null;
      }, { passive: true });
    }

    let variants = [];
    try {
      variants = JSON.parse(root.querySelector('[data-btl-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to parse Boutilora product variants.', error);
    }

    const optionGroups = [...root.querySelectorAll('[data-option-position]')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );

    const selectedOptions = () => optionGroups.map((group) => {
      const selected = group.querySelector('select') || group.querySelector('input:checked');
      return selected?.value || '';
    });

    const updateQuoteLinks = (variant, values) => {
      const title = root.querySelector('h1')?.textContent.trim() || '';
      const sku = variant?.sku || root.querySelector('[data-btl-sku]')?.textContent.trim() || '';
      root.querySelectorAll('[data-btl-quote-link]').forEach((link) => {
        const baseUrl = link.dataset.baseUrl;
        if (!baseUrl) return;
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('product', title);
        url.searchParams.set('sku', sku);
        url.searchParams.set('url', window.location.href.split('?')[0]);
        if (variant?.id) url.searchParams.set('variant', variant.id);
        optionGroups.forEach((group, index) => {
          const label = group.querySelector('legend, label')?.textContent.trim().toLowerCase() || `option_${index + 1}`;
          if (label.includes('color') || label.includes('colour')) url.searchParams.set('color', values[index] || '');
          else if (label.includes('size')) url.searchParams.set('size', values[index] || '');
          else url.searchParams.set(label.replace(/[^a-z0-9]+/g, '_'), values[index] || '');
        });
        link.href = url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.toString();
      });
    };

    const updateVariant = () => {
      const values = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === values[index])) || variants[0];
      if (!variant) return;
      const price = root.querySelector('[data-btl-price]');
      const sku = root.querySelector('[data-btl-sku]');
      const availability = root.querySelector('[data-btl-availability]');
      if (price) price.textContent = variant.price;
      if (sku) sku.textContent = variant.sku;
      if (availability) {
        availability.textContent = variant.available ? availability.dataset.availableText : availability.dataset.unavailableText;
      }
      if (variant.mediaId) {
        const mediaIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.mediaId));
        if (mediaIndex >= 0) showSlide(mediaIndex);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      updateQuoteLinks(variant, values);
    };

    optionGroups.forEach((group) => group.addEventListener('change', updateVariant));
    updateVariant();

    const favorite = root.querySelector('[data-btl-favorite]');
    if (favorite) {
      const storageKey = 'boutilora-favorites';
      const productHandle = favorite.dataset.productHandle;
      const readFavorites = () => {
        try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); }
        catch { return []; }
      };
      const renderFavorite = () => {
        const active = readFavorites().includes(productHandle);
        favorite.classList.toggle('is-active', active);
        favorite.innerHTML = `<span aria-hidden="true">${active ? '&#9829;' : '&#9825;'}</span> ${active ? 'Saved to Favorites' : 'Add to Favorites'}`;
        favorite.setAttribute('aria-pressed', String(active));
      };
      favorite.addEventListener('click', () => {
        const favorites = readFavorites();
        const index = favorites.indexOf(productHandle);
        if (index >= 0) favorites.splice(index, 1);
        else favorites.push(productHandle);
        localStorage.setItem(storageKey, JSON.stringify(favorites));
        renderFavorite();
      });
      renderFavorite();
    }
  };

  document.querySelectorAll('[data-btl-collection]').forEach(initCollection);
  document.querySelectorAll('[data-btl-product]').forEach(initProduct);
})();
